import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import config from '../config/config';

function PackageSearch() {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(null);
  const debounceRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!query.trim()) {
      setResults(null);
      setError('');
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setLoading(true);
      setError('');
      axios.get(`${config.BASE_URL}/packages/search`, { params: { q: query.trim() } })
        .then(r => setResults(r.data || []))
        .catch(err => {
          if (err.response?.status === 404) {
            setResults([]);
          } else {
            setError(err.response?.data?.error || err.message);
          }
        })
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const toggleExpand = (key) => setExpanded(prev => prev === key ? null : key);

  const hasCve = (pkg) => pkg.cveCount > 0 || pkg.advisoryCount > 0;

  return (
    <div className="App-body">
      <div className="ce-page-header">
        <h1 className="ce-page-title">📦 {t('packages.title')}</h1>
        <p className="ce-page-subtitle">Search across all indexed repositories for a specific package.</p>
      </div>

      <div className="ce-filter-bar mb-3">
        <input
          className="ce-search-input"
          type="text"
          placeholder={t('packages.placeholder')}
          value={query}
          onChange={e => setQuery(e.target.value)}
          autoFocus
        />
        {query && (
          <button className="ce-btn ce-btn--secondary ce-btn--sm" onClick={() => setQuery('')} type="button">✕</button>
        )}
      </div>

      {loading && (
        <div className="d-flex align-items-center gap-2 text-secondary">
          <div className="spinner-border spinner-border-sm" role="status" />
          <span>Searching…</span>
        </div>
      )}

      {error && <div className="alert alert-danger">{error}</div>}

      {results !== null && !loading && (
        results.length === 0 ? (
          <div className="ce-blank">
            <div className="ce-blank-icon">📦</div>
            <div className="ce-blank-title">No packages found</div>
            <div className="ce-blank-sub">Try a different search term.</div>
          </div>
        ) : (
          <>
            <p className="ce-filter-count mb-2">{results.length} package{results.length !== 1 ? 's' : ''} found</p>
            <div className="ce-card">
              <div className="ce-card-body--flush">
                <div className="ce-table-wrap">
                  <table className="ce-table">
                    <thead>
                      <tr>
                        <th>Package</th>
                        <th>Ecosystem</th>
                        <th>{t('packages.version')}</th>
                        <th>{t('packages.repo')}</th>
                        <th>{t('packages.vulnerabilities')}</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.map((pkg, i) => {
                        const key = `${pkg.name}@${pkg.version || i}`;
                        const isOpen = expanded === key;
                        return (
                          <React.Fragment key={key}>
                            <tr>
                              <td className="fw-semibold">{pkg.name}</td>
                              <td>
                                <span className="ce-tag">{pkg.ecosystem || '?'}</span>
                              </td>
                              <td><code className="small">{pkg.version || '—'}</code></td>
                              <td>{pkg.repoCount ?? (pkg.repos?.length ?? '?')}</td>
                              <td>
                                {hasCve(pkg)
                                  ? <span className="ce-sev ce-sev--critical">{pkg.cveCount ?? pkg.advisoryCount} CVE</span>
                                  : <span className="text-success small">✓</span>}
                              </td>
                              <td>
                                {pkg.repos && pkg.repos.length > 0 && (
                                  <button className="ce-btn ce-btn--sm ce-btn--secondary" onClick={() => toggleExpand(key)}>
                                    {isOpen ? '▲' : '▼'} Details
                                  </button>
                                )}
                              </td>
                            </tr>
                            {isOpen && pkg.repos && (
                              <tr className="table-light">
                                <td colSpan={6} className="px-4 py-2">
                                  <div className="ce-table-wrap">
                                    <table className="ce-table">
                                      <thead><tr><th>Repository</th><th>Commit</th></tr></thead>
                                      <tbody>
                                        {pkg.repos.map((r, j) => (
                                          <tr key={j} style={{ cursor: 'pointer' }} onClick={() => navigate(`/RepoDetails/${r.repoName || r}`)}>
                                            <td className="text-primary fw-semibold">{r.repoName || r}</td>
                                            <td><code className="small">{r.commitHash?.substring(0, 12) || '—'}</code></td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )
      )}
    </div>
  );
}

export default PackageSearch;
